<template src="./teaser.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import { callTurnToConfigFn } from 'util/turnto-utils'
import domUtils from 'util/dom-utils'
import globalStore from 'init/global-store'
import launchWriteReview from 'util/launch-write-review'
import { localizedThousandsInteger } from 'util/localization-utils'
import qaConfig from 'widgets/qa/qa.config'
import RatingStars from 'components/rating/RatingStars.vue'
import teaserConfig from './teaser.config'
import trackingService, { EventProperties } from 'services/tracking-service'
import ugcCountsService from 'services/api/ugc-counts'
import visibleObserver from 'util/visible-observer'

export default {
  data() {
    return {
      counts: {},
      globalStoreState: globalStore.state,
      isLoaded: false,
      previousSku: ''
    }
  },
  components: { 'tt-rating-stars': RatingStars },
  computed: {
    avgRating() {
      return teaserConfig.showAvgRating && !!this.reviews ?
        this.$msg('teaser.avgRating', [this.counts.avgRatingFormatted]) : ''
    },
    currentSku(): string {
      return this.globalStoreState.sku
    },
    comments() {
      const count = this.counts.comments
      if (!count || teaserConfig.excludeComments || qaConfig.showCheckoutChatterOnQAWidget) {
        return ''
      }
      return this.$msg('teaser.comments', [localizedThousandsInteger(count)], count)
    },
    hideReviews() {
      return teaserConfig.excludeReviews || this.counts.hideReviewsDisplay
    },
    questions() {
      const qCount = this.counts.questions
      if (!qCount || teaserConfig.excludeQa) {
        return ''
      }
      let msg = this.$msg('teaser.questions', [localizedThousandsInteger(qCount)], qCount)
      const aCount = this.counts.answers
      if (aCount) {
        msg += `${this.$msg('teaser.qa.separator')}${this.$msg('teaser.answers', [localizedThousandsInteger(aCount)], aCount)}`
      }
      return msg
    },
    reviews() {
      const count = this.counts.reviews
      if (!count || this.hideReviews) {
        return ''
      }
      return this.$msg('teaser.reviews', [localizedThousandsInteger(count)], count)
    },
    showOr() {
      return (this.comments || this.questions || this.reviews) && this.writeReviewText
    },
    starsRating() {
      return this.reviews ? this.counts.avgRating : 0
    },
    writeReviewText() {
      if (teaserConfig.excludeReviews || this.counts.hideReviewsDisplay) {
        return ''
      }
      return this.$msg(`teaser.${this.counts.reviews ? 'writeReview' : 'writeReviewFirst'}`)
    }
  },
  methods: {
    fetchData() {
      this.isLoaded = false
      if (this.currentSku) {
        const isFirstCall = this.currentSku !== this.previousSku
        ugcCountsService.ugcCounts({ sku: this.currentSku }).then((data) => {
          this.previousSku = this.currentSku
          this.counts = data
          this.isLoaded = true

          if (isFirstCall) {
            this.queueWebEvent('load')

            const rootElem = this.$el
            visibleObserver.register(rootElem, () => {
              this.queueWebEvent('view')
              visibleObserver.unregister(rootElem)
            })
          }

          nextTick(() => { callTurnToConfigFn('teaser', 'onFinish') })
        }).catch(() => {
          // nothing necessary as teaser won't display
        })
      }
    },
    getConfig() {
      return  window.turnToConfig.teaser || {}
    },
    toComments(e) {
      this.toUgc(e, 'showComments', 'c')
    },
    toQa(e) {
      this.toUgc(e, 'showQa', 'q')
    },
    toReviews(e) {
      this.toUgc(e, 'showReviews', 'r')
    },
    toUgc(e: Event, fnName, clickType) {
      e.preventDefault()
      if (this.getConfig()[fnName]) {
        this.getConfig()[fnName].call()
        this.queueWebEvent('click', clickType)
        return
      }
      const divId = (e.currentTarget as Element).getAttribute('href')
      const elem = document.querySelector(divId)
      if (elem) {
        domUtils.scrollToElement(elem)
        // not ideal, but since we don't know when scroll is done, just going to wait for a second.
        // also "should be" focusing on the correct heading, assuming it is always the first one
        // in the widget
        setTimeout(() => {
          domUtils.safeFocus(elem.querySelector('[role=heading]') as HTMLElement)
        }, 1000)
      }
      this.queueWebEvent('click', clickType)
    },
    writeReview() {
      launchWriteReview()
      this.queueWebEvent('click', 'wr')
    },
    queueWebEvent(event, clickType) {
      const ep = {
        rc: this.counts.reviews,
        ar: this.counts.avgRating,
        qc: this.counts.questions,
        ac: this.counts.answers,
        cc: this.counts.comments
      } as EventProperties
      if (clickType) {
        ep.ct = clickType
      }
      trackingService.queueEvent('teaser', event, globalStore.sku, ep)
    }
  },
  watch: {
    // whenever the currentSku changes, we need to reload the comments
    currentSku() {
      this.onFinishCalled = false
      this.fetchData()
    }
  },
  created() {
    this.fetchData()
  }
}
</script>

<style lang="scss" src="./teaser.scss"></style>
